/**
 * Vital City - single source of truth for chart + UI colors.
 *
 * Anchored on the CrowdVox brand: purple primary (#3D2969), with a
 * carefully-chosen secondary set that sits next to purple without
 * clashing and reads correctly across donut, bar, line, and map
 * surfaces.
 *
 * Rules of thumb:
 *  - Always use BRAND.* tokens. Never hardcode hex in chart configs.
 *  - For "positive / mixed / negative" sentiments use SENTIMENT.*.
 *  - For categorical series (themes, parties, age groups, etc.) use
 *    CATEGORICAL - it's ordered by visual weight (start of the array
 *    is the most prominent slot).
 *  - Party charts use PARTY.* for the politically-coded triad
 *    (Dem / Rep / Ind). DO NOT remap these.
 */

// Brand anchors - match Theme.js + Figma design tokens
export const BRAND = {
  PURPLE: "#3D2969",
  PURPLE_LIGHT: "#7851CF",
  PURPLE_MID: "#B9A4E6",    // light lavender, mid-step on the ramp
  PURPLE_SOFT: "#F2EBFC",   // wash background tint
  PURPLE_INK: "#2A1B49",    // hover / pressed state
  INK: "#1F2430",
  SUBTLE: "#83899F",
  BORDER: "#E2E8EE",
  PAGE_BG: "#FAFBFD",
};

// Three-step purple ramp for ordinal donuts where every slice should
// read as "on brand" rather than red/green sentiment-coded. Use this
// for direction / safety / stay donuts on the Overview tab - the
// reader should be able to glance at any donut and immediately
// recognise "CrowdVox dashboard" by colour alone.
export const PURPLE_RAMP = [
  "#3D2969",   // PURPLE (primary - biggest slice usually)
  "#7851CF",   // PURPLE_LIGHT (middle)
  "#D9CFEB",   // very soft purple (smallest / least-positive)
];

// Semantic sentiment scale. POS green is muted (not lime), NEG is
// terracotta (not fire-engine red). Both pair well with brand purple.
export const SENTIMENT = {
  POSITIVE: "#2E7D6B",   // muted teal-green
  POSITIVE_SOFT: "#A8D5C6",
  MIXED: "#9AA4B3",      // neutral slate
  NEGATIVE: "#B14B3E",   // terracotta
  NEGATIVE_SOFT: "#F0BFB6",
  AMBER: "#D97706",      // attention without alarm (used for medium confidence etc.)
};

// Political party coding. Locked.
//   Dem  → blue   (#2563EB)
//   Rep  → red    (#E11D48)
//   Ind  → grey   (#6B7280)
// These are intentionally NOT in the categorical palette - politics
// has its own semantic mapping that overrides brand styling.
export const PARTY = {
  Democrat: "#2563EB",
  Republican: "#E11D48",
  Independent: "#6B7280",
  // Fallbacks for snapshot-level oddities
  "Prefer not to answer": "#C7C9D1",
  Unknown: "#C7C9D1",
  Other: "#C7C9D1",
};

// Ordered categorical palette for theme/age/race/etc. cuts. First
// color is brand purple so the most-prominent slot reinforces brand.
// Subsequent colors step away from purple while staying in the
// CrowdVox-extended range: teal, gold, slate, terracotta, lavender,
// olive.
export const CATEGORICAL = [
  BRAND.PURPLE,         // #3D2969 brand
  "#2E7D6B",            // teal (= SENTIMENT.POSITIVE)
  "#B68B3F",            // gold (warm contrast, not yellow)
  "#7B6FBE",            // soft lavender (sibling of purple)
  "#B14B3E",            // terracotta (= SENTIMENT.NEGATIVE)
  "#4F6071",            // slate
  "#8C9F4B",            // olive
  "#6D5F8E",            // muted plum
];

// Wave-trend series. Tight purple-family palette so the chart reads
// as on-brand. Direction (the headline) is brand primary; the five
// concern/sentiment lines step DOWN the purple ramp into lavender +
// plum + slate-purple. One contrast color (terracotta) is reserved
// for the most-negative flag (economic_pressure) so it visually pops
// out from the purple cluster - that's the lede a reader should
// see first.
export const WAVE_SERIES_COLOR = {
  direction_net_score:    "#3D2969",   // brand purple - headline
  economic_pressure_pct:  "#B14B3E",   // terracotta - reserved contrast for the dominant concern
  safety_concern_pct:     "#7851CF",   // brand light purple
  housing_stress_pct:     "#B9A4E6",   // soft lavender
  optimism_pct:           "#6D5F8E",   // muted plum
  mental_health_pct:      "#4F6071",   // slate (only neutral grey)
};

// Used by the choropleth map's diverging scale. From most-negative
// composite5 (deep red-brown) through neutral (cool grey) to most-
// positive (deep teal). Steps are colorblind-friendly.
export const COMPOSITE_SCALE = [
  { gte: 0.5,  label: "Positive",          color: "#2E7D6B" },
  { gte: 0,    lt: 0.5, label: "Neutral",  color: "#CFD6DF" },
  { gte: -1,   lt: 0,   label: "Mildly negative", color: "#E4B7A8" },
  { gte: -2,   lt: -1,  label: "Negative",       color: "#D0876E" },
  { gte: -3,   lt: -2,  label: "Strongly negative", color: "#B14B3E" },
  { lt: -3,    label: "Very negative",     color: "#7D2A1E" },
];

// Single-spot exports for legacy code paths that import `POS`/`NEG`/`MIX`.
// Encourage new code to use the structured tokens above.
export const POS = SENTIMENT.POSITIVE;
export const NEG = SENTIMENT.NEGATIVE;
export const MIX = SENTIMENT.MIXED;

// ──────────────────────────────────────────────────────────────────────────
// 2026 BLUE rebrand (Figma node 6116-5118). The VitalCity dashboard primary
// moved from brand purple to blue. Use PRIMARY for active states / buttons /
// filters / bar fills / focus; the BLUE_RAMP for donut slices; PRIMARY_SOFT
// for active chip washes; HERO_GRADIENT for the top banner.
// Sentiment (POS/NEG) and party (PARTY.*) colours are unchanged.
export const PRIMARY = "#2E45DA";        // primary blue
export const PRIMARY_DARK = "#243AB8";   // hover / pressed
export const PRIMARY_MID = "#506ED3";    // figma mid blue
export const PRIMARY_MUTED = "#749ACC";  // figma muted blue
export const PRIMARY_SOFT = "#D8E0FF";   // light blue wash / active chip bg
export const BLUE_RAMP = ["#2E45DA", "#749ACC", "#506ED3"]; // donut slices (figma order)
export const HERO_GRADIENT = "linear-gradient(111.35deg, #749ACC 7%, #2E45DA 93%)";
